import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Save } from 'lucide-react';
import { Button } from '../ui/Button';
import { FormField } from '../ui/FormField';
import { Alert } from '../ui/Alert';
import { quoteService } from '../../lib/database/quotes';
import { clientService } from '../../lib/database/clients';

interface QuoteItem {
  id?: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface EditQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  quote: any;
}

export const EditQuoteModal: React.FC<EditQuoteModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  quote,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [clients, setClients] = useState<any[]>([]);

  // Form state
  const [clientId, setClientId] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const [taxRate, setTaxRate] = useState(18);
  const [status, setStatus] = useState('draft');
  const [items, setItems] = useState<QuoteItem[]>([
    { description: '', quantity: 1, rate: 0, amount: 0 }
  ]);

  // Load clients and initialize form data
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load clients
        const { data: clientsData } = await clientService.getClients();
        if (clientsData) {
          setClients(clientsData);
        }

        // Initialize form with quote data
        if (quote) {
          setClientId(quote.client_id || '');
          setValidUntil(quote.valid_until || '');
          setNotes(quote.notes || '');
          setTaxRate(Number(quote.tax_rate) || 18);
          setStatus(quote.status || 'draft');
          
          // Set items from quote_items
          if (quote.quote_items && quote.quote_items.length > 0) {
            setItems(quote.quote_items.map((item: any) => ({
              id: item.id,
              description: item.description || '',
              quantity: Number(item.quantity) || 1,
              rate: Number(item.rate) || 0,
              amount: Number(item.amount) || 0,
            })));
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    if (isOpen && quote) {
      loadData();
    }
  }, [isOpen, quote]);

  // Calculate totals
  const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
  const taxAmount = (subtotal * taxRate) / 100;
  const total = subtotal + taxAmount;

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, rate: 0, amount: 0 }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index: number, field: keyof QuoteItem, value: string | number) => {
    const updatedItems = [...items];
    
    // Handle quantity as integer only
    if (field === 'quantity') {
      const intValue = parseInt(value.toString()) || 1;
      updatedItems[index] = { ...updatedItems[index], [field]: intValue };
      updatedItems[index].amount = intValue * updatedItems[index].rate;
    } else {
      updatedItems[index] = { ...updatedItems[index], [field]: value };
      
      // Recalculate amount when rate changes
      if (field === 'rate') {
        updatedItems[index].amount = updatedItems[index].quantity * Number(value);
      }
    }
    
    setItems(updatedItems);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validation
      if (!clientId) {
        setError('Please select a client');
        return;
      }

      if (!validUntil) {
        setError('Please set a valid until date');
        return;
      }

      if (items.some(item => !item.description.trim())) {
        setError('Please fill in all item descriptions');
        return;
      }

      if (items.some(item => item.rate <= 0)) {
        setError('All items must have a rate greater than 0');
        return;
      }

      if (items.some(item => item.quantity <= 0)) {
        setError('All items must have a quantity greater than 0');
        return;
      }

      // Update quote
      const { data: updatedQuote, error: quoteError } = await quoteService.updateQuote(quote.id, {
        client_id: clientId,
        valid_until: validUntil,
        subtotal,
        tax_rate: taxRate,
        tax_amount: taxAmount,
        total,
        status: status as any,
        notes: notes.trim() || null,
      });

      if (quoteError || !updatedQuote) {
        setError('Failed to update quote: ' + (quoteError?.message || 'Unknown error'));
        return;
      }

      // Update quote items (delete existing and create new ones)
      const quoteItems = items.map(item => ({
        quote_id: quote.id,
        description: item.description.trim(),
        quantity: item.quantity,
        rate: item.rate,
        amount: item.amount,
      }));

      // For simplicity, we'll delete all existing items and create new ones
      // In a production app, you might want to update existing items individually
      const { error: itemsError } = await quoteService.createQuoteItems(quoteItems);

      if (itemsError) {
        setError('Quote updated but failed to update items: ' + itemsError.message);
        return;
      }

      setSuccess('Quote updated successfully!');
      
      // Close modal and refresh after delay
      setTimeout(() => {
        onSuccess();
        onClose();
        setError('');
        setSuccess('');
      }, 1500);

    } catch (error: any) {
      setError('An unexpected error occurred: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !quote) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Edit Quote</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && <Alert type="error" message={error} />}
          {success && <Alert type="success" message={success} />}

          {/* Quote Number Display */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Quote Number</p>
            <p className="text-lg font-semibold text-gray-900">{quote.quote_number}</p>
          </div>

          {/* Client, Valid Until, and Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FormField label="Client" required>
              <select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">Select a client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name} - {client.company || 'No company'}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Valid Until" required>
              <input
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </FormField>

            <FormField label="Status" required>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </FormField>
          </div>

          {/* Tax Rate */}
          <FormField label="Tax Rate (%)" helpText="Default is 18% GST">
            <input
              type="number"
              value={taxRate}
              onChange={(e) => setTaxRate(Number(e.target.value))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              min="0"
              max="100"
              step="0.01"
            />
          </FormField>

          {/* Quote Items */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Quote Items</h3>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                icon={Plus}
                onClick={addItem}
              >
                Add Item
              </Button>
            </div>

            <div className="space-y-4">
              {items.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-12 md:col-span-5">
                    <FormField label="Description" required>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Item description"
                        required
                      />
                    </FormField>
                  </div>

                  <div className="col-span-4 md:col-span-2">
                    <FormField label="Qty" required>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        min="1"
                        step="1"
                        required
                      />
                    </FormField>
                  </div>

                  <div className="col-span-4 md:col-span-2">
                    <FormField label="Rate (₹)" required>
                      <input
                        type="number"
                        value={item.rate}
                        onChange={(e) => updateItem(index, 'rate', Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        min="0.01"
                        step="0.01"
                        required
                      />
                    </FormField>
                  </div>

                  <div className="col-span-3 md:col-span-2">
                    <FormField label="Amount (₹)">
                      <input
                        type="text"
                        value={`₹${item.amount.toFixed(2)}`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                        readOnly
                      />
                    </FormField>
                  </div>

                  <div className="col-span-1">
                    <Button
                      type="button"
                      variant="error"
                      size="sm"
                      icon={Trash2}
                      onClick={() => removeItem(index)}
                      disabled={items.length === 1}
                      className="w-full"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">₹{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tax ({taxRate}%):</span>
                <span className="font-medium">₹{taxAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Total:</span>
                <span>₹{total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <FormField label="Notes" helpText="Optional notes for the quote">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows={3}
              placeholder="Additional notes or terms..."
            />
          </FormField>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              icon={Save}
              loading={loading}
              disabled={loading}
            >
              Update Quote
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};